import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'already-verified'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        await verifyEmail(token);
        setStatus('success');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err: any) {
        setStatus('error');
        if (err.response?.data?.code === 'AUTH_006') {
          setErrorMessage('This verification link is invalid or has expired. Please request a new verification email.');
        } else if (err.response?.data?.code === 'AUTH_007') {
          setStatus('already-verified');
          return;
        } else {
          setErrorMessage('An error occurred during email verification. Please try again.');
        }
      }
    };

    verify();
  }, [token, verifyEmail, navigate]);

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          {status === 'loading' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Verifying Your Email...
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we verify your email address.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Email Verified Successfully!
              </h2>
              <p className="mt-2 text-sm text-gray-600 mb-6">
                Your email has been verified. You will be redirected to the login page in a few seconds.
              </p>
              <Link
                to="/login"
                className="btn-primary w-full inline-block"
              >
                Go to Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Verification Failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {errorMessage}
              </p>
              <div className="mt-6 space-y-3">
                {errorMessage.includes('expired') && (
                  <Link
                    to="/resend-verification"
                    className="btn-primary w-full inline-block"
                  >
                    Request New Verification Link
                  </Link>
                )}
                <Link
                  to="/login"
                  className="btn-secondary w-full inline-block"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          )}

          {status === 'already-verified' && (
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Already Verified
              </h2>
              <p className="mt-2 text-sm text-gray-600 mb-6">
                Your email has already been verified. You can proceed to log in.
              </p>
              <Link
                to="/login"
                className="btn-primary w-full inline-block"
              >
                Go to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;